import { useState, useEffect } from 'react'
import AssetMap from 'components/containers/assets/AssetMap'
import AssetMapList from 'components/containers/assets/AssetMapList'
import { Grid, Box } from '@mui/material'
import { useContentHeight } from 'utils/custom-hooks/useContentHeight'
import { AssetPosition } from 'utils/test/data-mocked/assetPositions'
import { useGetAssetsByCategoryQuery } from 'services/physicalAssets'
import { useValidateAddressesMutation } from 'services/validateAddress'
import { ASSET_CATEGORY } from 'utils/constants'

interface ContainerAsset {
  assetStatus?: {
    currentLocation?: {
      coordinates?: { coordinates: number[] }
      name?: string
      id?: string
      customer?: {
        displayName?: string
      }
    }
  }
  assetName?: string
  type?: string
  daysOnSite?: string
  id?: string
}

const AssetMapContainer = () => {
  const [selectedAsset, setSelectedAsset] = useState<AssetPosition>()
  const { contentHeight } = useContentHeight()

  const [validateAddresses, { isLoading: isValidating }] = useValidateAddressesMutation()
  const { data: containerAssetData, isLoading } = useGetAssetsByCategoryQuery({
    assetCategory: ASSET_CATEGORY.CONTAINER,
  })

  // Function to validate addresses for assets without valid coordinates
  const validateAddressWithoutCoordinates = async (assets: ContainerAsset[]) => {
    const locationIds = assets
      .filter(asset => {
        const coordinates = asset.assetStatus?.currentLocation?.coordinates?.coordinates
        return !coordinates || coordinates.length !== 2
      })
      .map(asset => asset.assetStatus?.currentLocation?.id)
      .filter((id): id is string => !!id)

    if (locationIds.length > 0) {
      try {
        await validateAddresses({ body: { locationIds } }).unwrap()
      } catch (error) {
        console.error('Error validating addresses:', error)
      }
    }
  }

  useEffect(() => {
    validateAddressWithoutCoordinates(containerAssetData?.Items || [])
  }, [containerAssetData, validateAddresses])

  const assetPositions: AssetPosition[] = (containerAssetData?.Items || []).map((asset: ContainerAsset, index) => {
    const coordinates = asset.assetStatus?.currentLocation?.coordinates?.coordinates ?? []
    const locationId = asset.assetStatus?.currentLocation?.id
    const hasValidCoordinates = coordinates && coordinates.length === 2

    return {
      id: index + 1,
      coords: hasValidCoordinates ? ([coordinates[1], coordinates[0]] as [number, number]) : undefined,
      label: asset.assetStatus?.currentLocation?.name ?? 'Unknown Location',
      customerName: asset.assetStatus?.currentLocation?.customer?.displayName ?? 'Unknown Customer',
      assetName: asset.assetName ?? `Container ${index}`,
      assetType: asset.type ?? '40-yd',
      daysOnSite: Number(asset.daysOnSite) || 0,
      asset: 'container' as const,
      assetId: asset.id ?? '',
      locationId,
    }
  })

  // Filter positions to include only those with valid coordinates for the map display
  const mapPositions = assetPositions.filter((pos): pos is AssetPosition & { coords: [number, number] } => !!pos.coords)

  return (
    <Grid p={1} sx={{ height: contentHeight }} container spacing={2}>
      <Grid item xs={4} sx={{ height: '100%' }}>
        <Box sx={{ height: '100%', backgroundColor: 'background.paper', borderRadius: '8px' }}>
          <AssetMapList
            positions={assetPositions}
            onAssetSelect={setSelectedAsset}
            selectedAsset={selectedAsset}
            loading={isLoading}
          />
        </Box>
      </Grid>
      <Grid item xs={8} sx={{ height: '100%' }}>
        <Box sx={{ height: '100%', borderRadius: '8px', overflow: 'hidden' }}>
          <AssetMap positions={mapPositions} selectedAsset={selectedAsset} />
        </Box>
      </Grid>
    </Grid>
  )
}

export default AssetMapContainer
